import { FC, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useObjectEffect, useTrustedState } from '@/Components/Hooks';
import { DatePicker, DateSwitcher } from '@/Components/UI';
import { Switch } from '@/Components/UI/DateSwitcher/types';
import { Tools } from '@/Services';
import { Maybe } from '@/Types/Maybe';

import { DateFilterProps } from './types';

/**
 * TODO: Extract to independent component.
 */
export const DateFilter: FC<DateFilterProps> = ({
    full,
    switcher,
    onChange,
    value,
    initialValue = [undefined, undefined],
    initialSwitchValue = Switch.Month,
}) => {
    const [dateRange, setDateRange, isTrusted] = useTrustedState<[number | undefined, number | undefined]>(initialValue);
    const [offset, setOffset] = useState<Maybe<Switch>>(initialSwitchValue);
    const [isOffsetChanged, setOffsetChanged] = useState<string>('');

    const handleDatePick = (range?: [number | undefined, number | undefined]) => {
        const [from, to] = range ?? [undefined, undefined];

        const toUtcTimestamp = (d: number | undefined, dayEnd = false) => {
            if (!d) return undefined;

            const date = new Date(d);
            return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), dayEnd ? 23 : 0, dayEnd ? 59 : 0, dayEnd ? 59 : 0);
        };

        if (!from && !to) {
            setOffset(Switch.Month);
        }

        setDateRange([toUtcTimestamp(from), toUtcTimestamp(to, true)]);
        setOffset(initialSwitchValue);
    };

    const handleOffsetPick = (offset: Switch) => {
        if (!offset) {
            setDateRange([undefined, undefined]);
            return;
        }

        setOffsetChanged(uuid());

        setDateRange(() => {
            switch (offset) {
                case Switch.Day: {
                    const today = Tools.date.create();
                    return [
                        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
                        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59),
                    ];
                }
                case Switch.Month: {
                    const currentDate = Tools.date.create();
                    return [
                        Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1),
                        Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.lastDay(), 23, 59, 59),
                    ];
                }
                case Switch.Year: {
                    const year = new Date().getUTCFullYear();
                    return [Date.UTC(year, 0, 1), Date.UTC(year, 11, Tools.date.create(Date.UTC(year, 11, 1)).lastDay(), 23, 59, 59)];
                }
            }
        });
    };

    useObjectEffect(() => {
        if (onChange && isTrusted) onChange(dateRange);
    }, [dateRange]);

    useObjectEffect(() => {
        if (!value) {
            return;
        }

        setDateRange(value, false);
    }, [value]);

    return (
        <>
            {switcher && <DateSwitcher initialValue={initialSwitchValue} value={offset} onChange={handleOffsetPick} />}
            <DatePicker value={dateRange} onChange={handleDatePick} full={full} key={isOffsetChanged} />
        </>
    );
};
